/**
 * API 端点与基础地址集中配置。
 *
 * 所有端点路径以 `/api` 前缀书写，与后端 REST 契约保持一致。
 * `VITE_API_BASE_URL` 应设为 host 根（如 `/` 或 `http://localhost:8787`），
 * 不要带 `/api` 后缀，否则会与本文件中端点路径的 `/api` 前缀重复。
 *
 * @module config/api
 */

/** API 基础地址（host 根）。默认空字符串表示同源。 */
export const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

/** 统一 API 端点集合。 */
export const API = {
  auth: {
    register: '/api/auth/register',
    login: '/api/auth/login',
    refresh: '/api/auth/refresh',
    logout: '/api/auth/logout',
    me: '/api/auth/me',
  },
  user: {
    profile: '/api/user/profile',
    balance: '/api/user/balance',
  },
  apikey: {
    list: '/api/apikeys',
    create: '/api/apikeys',
    detail: (id: number | string): string => `/api/apikeys/${id}`,
  },
  usage: {
    list: '/api/usage',
    overview: '/api/usage/overview',
    daily: '/api/usage/daily',
  },
  order: {
    recharge: '/api/orders/recharge',
    package: '/api/orders/package',
    list: '/api/orders',
    usdtConfirm: (orderNo: string): string =>
      `/api/orders/${orderNo}/usdt-confirm`,
  },
  package: {
    list: '/api/packages',
    purchase: (id: number | string): string => `/api/packages/${id}/purchase`,
    myPackages: '/api/my-packages',
  },
  refund: {
    create: '/api/refunds',
    list: '/api/refunds',
  },
  team: {
    create: '/api/teams',
    mine: '/api/teams/mine',
    members: (id: number | string): string => `/api/teams/${id}/members`,
    memberDetail: (teamId: number | string, memberId: number | string): string =>
      `/api/teams/${teamId}/members/${memberId}`,
    billingMode: (id: number | string): string => `/api/teams/${id}/billing-mode`,
  },
  admin: {
    channels: '/api/admin/channels',
    channelDetail: (id: number | string): string => `/api/admin/channels/${id}`,
    channelHealth: (id: number | string): string =>
      `/api/admin/channels/${id}/health`,
    modelMappings: '/api/admin/model-mappings',
    modelMappingDetail: (id: number | string): string =>
      `/api/admin/model-mappings/${id}`,
    pendingRefunds: '/api/admin/refunds/pending',
    refundApprove: (id: number | string): string =>
      `/api/admin/refunds/${id}/approve`,
    refundReject: (id: number | string): string =>
      `/api/admin/refunds/${id}/reject`,
    refundComplete: (id: number | string): string =>
      `/api/admin/refunds/${id}/complete`,
    users: '/api/admin/users',
    userDetail: (id: number | string): string => `/api/admin/users/${id}`,
    configs: '/api/admin/configs',
    configDetail: (key: string): string => `/api/admin/configs/${key}`,
  },
} as const;
